import {
  containsAccess,
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
  isGlobalManage,
  type PermissionContext,
  type PermissionKey,
} from '@aysar/contracts';
import { useMemo } from 'react';

import { useSession } from '@/shared/hooks/useSession/useSession';
import { toPermissionContext } from '@/shared/utils/permission-context/permission-context.util';

export interface UseHasPermission {
  can: (key: PermissionKey) => boolean;
  canAll: (keys: readonly PermissionKey[]) => boolean;
  canAny: (keys: readonly PermissionKey[]) => boolean;
  /**
   * Whether the operator may **hand this key out** — put it in a role, or grant it
   * directly (N28's containment, plus the super-admin restriction on the global
   * wildcard).
   *
   * The api is the authority; this exists so the role editor does not render
   * checkboxes it knows the save will refuse.
   */
  mayGrant: (key: PermissionKey) => boolean;
  /**
   * Whether the operator's authority **contains** another account's — N28's target
   * confinement, the rule that decides whether they may act on that row at all.
   */
  contains: (subject: PermissionContext) => boolean;
  isSuperAdmin: boolean;
  isAuthenticated: boolean;
}

/**
 * The ONE permission evaluator on the client (N22).
 *
 * It calls `hasPermission`/`hasAll`/`hasAny` from `@aysar/contracts` — the very
 * functions `PermissionsGuard` calls on the api — so the button the operator can
 * see and the endpoint behind it agree about wildcards and about deny-wins.
 * Nothing in this app may write `permissions.includes(...)`; that is the second
 * implementation N22 exists to forbid, and it would get deny semantics wrong on
 * its first day.
 *
 * Signed out means `can()` is false for everything, never a crash.
 */
export function useHasPermission(): UseHasPermission {
  const { claims, isAuthenticated, isSuperAdmin } = useSession();

  return useMemo(() => {
    const context = toPermissionContext(claims);

    if (context === null) {
      return {
        can: () => false,
        canAll: () => false,
        canAny: () => false,
        mayGrant: () => false,
        contains: () => false,
        isSuperAdmin: false,
        isAuthenticated: false,
      };
    }

    return {
      can: (key: PermissionKey) => hasPermission(context, key),
      canAll: (keys: readonly PermissionKey[]) => hasAllPermissions(context, keys),
      canAny: (keys: readonly PermissionKey[]) => hasAnyPermission(context, keys),
      // The global wildcard is a super admin's alone — `hasPermission` would say
      // yes to a `manage` holder, and the api would then refuse them
      // (`super_admin_grant_restricted`). Mirroring the api's own rule here is the
      // difference between a disabled checkbox and a 403.
      mayGrant: (key: PermissionKey) =>
        isSuperAdmin || (!isGlobalManage(key) && hasPermission(context, key)),
      contains: (subject: PermissionContext) => containsAccess(context, subject),
      isSuperAdmin,
      isAuthenticated,
    };
  }, [claims, isAuthenticated, isSuperAdmin]);
}
